// Configuration management for the Kochaj Ludzi application
// This file handles loading configuration from the public config.js file

interface ApiConfig {
  baseUrl: string;
  username: string;
  password: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
  endpoints: {
    images: string;
    upload: string;
    health: string;
  };
  headers: {
    accept: string;
    contentType: string;
    userAgent: string;
  };
  cors: {
    mode: string;
    credentials: string;
  };
}

interface UploadConfig {
  maxFileSize: number;
  maxTotalSize: number;
  maxFiles: number;
  allowedTypes: string[];
  fieldName: string;
}

interface GalleryConfig {
  itemsPerPage: number;
  enableInfiniteScroll: boolean;
  preloadThreshold: number;
}

interface FeaturesConfig {
  enableUpload: boolean;
  enableLikes: boolean;
  enableReports: boolean;
  enableComments: boolean;
  enableDebugLogs: boolean;
}

interface UIConfig {
  enableAnimations: boolean;
  theme: string;
  toastPosition: string;
}

interface EnvironmentConfig {
  development: {
    useProxy: boolean;
    proxyPath: string;
    enableDetailedLogs: boolean;
  };
  production: {
    useProxy: boolean;
    enableDetailedLogs: boolean;
  };
}

interface AppConfig {
  api: ApiConfig;
  upload: UploadConfig;
  gallery: GalleryConfig;
  features: FeaturesConfig;
  ui: UIConfig;
  environments: EnvironmentConfig;
}

// Default configuration as fallback - NO HARDCODED API DATA!
const defaultConfig: AppConfig = {
  api: {
    baseUrl: '/api', // Default to proxy
    username: '', // Will be loaded from config.js
    password: '', // Will be loaded from config.js
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
    endpoints: {
      images: 'images',
      upload: 'images',
      health: 'health'
    },
    headers: {
      accept: 'application/json',
      contentType: 'application/json',
      userAgent: 'Kochaj-Ludzi-App/1.0'
    },
    cors: {
      mode: 'cors',
      credentials: 'omit'
    }
  },
  upload: {
    maxFileSize: 10 * 1024 * 1024,
    maxTotalSize: 10 * 1024 * 1024,
    maxFiles: 5,
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    fieldName: 'nazwa'
  },
  gallery: {
    itemsPerPage: 12,
    enableInfiniteScroll: true,
    preloadThreshold: 3
  },
  features: {
    enableUpload: true,
    enableLikes: false,
    enableReports: false,
    enableComments: false,
    enableDebugLogs: true
  },
  ui: {
    enableAnimations: true,
    theme: 'light',
    toastPosition: 'top-center'
  },
  environments: {
    development: {
      useProxy: true,
      proxyPath: '/api',
      enableDetailedLogs: true
    },
    production: {
      useProxy: false,
      enableDetailedLogs: false
    }
  }
};

// Function to get configuration from window.APP_CONFIG or fallback to default
function getAppConfig(): AppConfig {
  try {
    // Check if window.APP_CONFIG exists (loaded from public/config.js)
    if (typeof window !== 'undefined' && (window as any).APP_CONFIG) {
      const config = (window as any).APP_CONFIG;
      
      if (config.features?.enableDebugLogs) {
        console.log('✅ Załadowano konfigurację z config.js:', {
          ...config,
          api: {
            ...config.api,
            password: '***' // Ukryj hasło w logach
          }
        });
      }
      
      return config;
    }
  } catch (error) {
    console.warn('⚠️ Nie można załadować config.js, używam konfiguracji domyślnej:', error);
    console.warn('📋 Sprawdź czy plik public/config.js istnieje i zawiera window.APP_CONFIG');
  }
  
  console.log('🔧 Używam konfiguracji domyślnej');
  return defaultConfig;
}

// Export the configuration
export const appConfig = getAppConfig();

// Helper functions - WITH Authorization header
export const getApiUrl = (endpoint: string): string => {
  const baseUrl = appConfig.api.baseUrl.replace(/\/$/, ''); // Remove trailing slash
  const endpointPath = appConfig.api.endpoints[endpoint as keyof typeof appConfig.api.endpoints] || endpoint;
  const cleanEndpoint = endpointPath.replace(/^\//, ''); // Remove leading slash
  return `${baseUrl}/${cleanEndpoint}`;
};

export const getRequestHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Accept': appConfig.api.headers.accept,
    'Content-Type': appConfig.api.headers.contentType,
    'User-Agent': appConfig.api.headers.userAgent
  };
  
  // Add Authorization header if username and password are configured
  if (appConfig.api.username && appConfig.api.password) {
    const credentials = btoa(`${appConfig.api.username}:${appConfig.api.password}`);
    headers['Authorization'] = `Basic ${credentials}`;
    
    if (appConfig.features.enableDebugLogs) {
      console.log('🔑 Dodano nagłówek autoryzacji dla użytkownika:', appConfig.api.username);
    }
  } else if (appConfig.features.enableDebugLogs) {
    console.warn('⚠️ Brak danych autoryzacji - API może zwrócić błąd 401');
  }
  
  return headers;
};

export const getRequestOptions = (): RequestInit => {
  return {
    mode: appConfig.api.cors.mode as RequestMode,
    credentials: appConfig.api.cors.credentials as RequestCredentials
  };
};

// Function to check if API is configured properly
export const isApiConfigured = (): boolean => {
  const isConfigured = !!(appConfig.api.baseUrl && appConfig.api.username && appConfig.api.password);
  
  if (!isConfigured && appConfig.features.enableDebugLogs) {
    console.error('❌ API nie jest skonfigurowane! Sprawdź config.js:', {
      baseUrl: appConfig.api.baseUrl || 'BRAK',
      username: appConfig.api.username || 'BRAK',
      password: appConfig.api.password ? '***' : 'BRAK'
    });
  }
  
  return isConfigured;
};

// Function to get current environment
export const getCurrentEnvironment = (): 'development' | 'staging' | 'production' => {
  if (typeof window === 'undefined') return 'development';
  
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || 
      hostname.includes('webcontainer') || hostname.includes('stackblitz') ||
      hostname.includes('local-credentialless')) {
    return 'development';
  } else if (hostname.includes('staging')) {
    return 'staging';
  }
  return 'production';
};

// Function to log configuration (used in main.tsx)
export const logConfig = (): void => {
  if (!appConfig.features.enableDebugLogs) return;
  
  console.log('📋 Application Configuration:', {
    environment: getCurrentEnvironment(),
    apiConfigured: isApiConfigured(),
    features: appConfig.features,
    upload: appConfig.upload,
    gallery: appConfig.gallery,
    ui: appConfig.ui,
    api: {
      baseUrl: appConfig.api.baseUrl,
      timeout: appConfig.api.timeout,
      retryAttempts: appConfig.api.retryAttempts,
      hasCredentials: !!(appConfig.api.username && appConfig.api.password)
    }
  });
};

// Function to get upload limits (used in Home.tsx)
export const getUploadLimits = (): UploadConfig => {
  return appConfig.upload;
};

// Function to check if a feature is enabled (used in Home.tsx)
export const isFeatureEnabled = (featureName: keyof FeaturesConfig): boolean => {
  return appConfig.features[featureName];
};

// Function to check if debug logs are enabled
export const isDebugEnabled = (): boolean => {
  return appConfig.features.enableDebugLogs;
};